import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import ThemedTypography from "./ThemedTypography";
import ThemedSelect from "./ThemedSelect";
import { decoratedFetch } from "../req_utils";
import {
  DARK_THEME_INPUT_OUTLINE_COLOR,
  DARK_THEME_TEXT_COLOR,
  DARK_THEME_DISABLED_TEXT_COLOR,
  DARK_THEME_BODY_COLOR,
  LIGHT_THEME_BODY_COLOR,
} from "../constants";

const MobileLaborTab = ({ open, projectId, setSnackbarMessage }) => {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [laborItems, setLaborItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backdropOpen, setBackdropOpen] = useState(false);

  const listProposals = () => {
    if (!projectId) {
      return;
    }

    decoratedFetch(`/list_proposals/${projectId}?mobile=true`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setProposals(data || []);
        if (data && data.length > 0) {
          setSelectedProposal(data[0]);
        }
      })
      .catch((error) => {
        console.error(`Error listing proposals: ${error}`);
        setProposals([]);
      });
  };

  const listProjectLaborItems = () => {
    if (!selectedProposal?.id) {
      setLaborItems([]);
      setLoading(false);
      return;
    }

    decoratedFetch(
      `/list_project_demo_items?proposal_id=${selectedProposal.id}`,
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setLaborItems(data || []);
        setLoading(false);
        setBackdropOpen(false);
      })
      .catch((error) => {
        setSnackbarMessage("Failed to load labor items");
        setLoading(false);
        setBackdropOpen(false);
        console.error(`Error listing labor items: ${error}`);
      });
  };

  useEffect(() => {
    if (!open) return;
    listProposals();
  }, [open, projectId]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listProjectLaborItems();
  }, [open, selectedProposal]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  const getLaborItemDisplayText = (laborItem) => {
    const parts = [];

    if (laborItem.labor_item_type?.name)
      parts.push(laborItem.labor_item_type.name);
    if (laborItem.demo_item_type) parts.push(laborItem.demo_item_type);

    return parts.join(" - ");
  };

  const getLaborItemSecondaryText = (laborItem) => {
    const details = [];

    if (laborItem.quantity) {
      const unit = laborItem.size_unit?.name || "";
      details.push(`Qty: ${laborItem.quantity} ${unit}`);
    }

    if (laborItem.size_unit?.name) {
      details.push(`Unit: ${laborItem.size_unit.name}`);
    }

    if (laborItem.unit_price) {
      details.push(`Unit Price: ${formatPrice(laborItem.unit_price)}`);
    }

    if (laborItem.total_price) {
      details.push(`Total: ${formatPrice(laborItem.total_price)}`);
    }

    return details.join(" | ");
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={backdropOpen}
        onClick={() => setBackdropOpen(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: darkThemeEnabled
            ? DARK_THEME_BODY_COLOR
            : LIGHT_THEME_BODY_COLOR,
          pt: "15px",
          px: "15px",
          pb: "5px",
        }}
      >
        <ThemedSelect
          value={selectedProposal?.id || ""}
          onChange={(e) => {
            const proposal = proposals.find((p) => p.id === e.target.value);
            setSelectedProposal(proposal);
          }}
          label="Proposal"
          size="small"
          fullWidth
          renderValue={(selectedId) => {
            const selected = proposals.find(
              (proposal) => proposal.id === selectedId,
            );
            return selected ? selected.name : "";
          }}
          MenuProps={{
            disableScrollLock: true,
            PaperProps: {
              style: {
                maxHeight: "300px",
                overflowY: "auto",
              },
            },
          }}
        >
          {proposals.map((proposal) => (
            <MenuItem key={proposal?.id} value={proposal?.id}>
              {proposal?.name}
            </MenuItem>
          ))}
        </ThemedSelect>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {laborItems?.length ? (
          <List sx={{ py: 0 }}>
            {laborItems.map((laborItem, index) => (
              <React.Fragment key={laborItem.id}>
                <ListItem
                  sx={{
                    p: 2,
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <ListItemText
                    primary={getLaborItemDisplayText(laborItem)}
                    secondary={getLaborItemSecondaryText(laborItem)}
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: "bold",
                        color: darkThemeEnabled
                          ? DARK_THEME_TEXT_COLOR
                          : "inherit",
                        fontSize: "1rem",
                        lineHeight: 1.4,
                      },
                    }}
                    secondaryTypographyProps={{
                      sx: {
                        color: darkThemeEnabled
                          ? DARK_THEME_DISABLED_TEXT_COLOR
                          : "inherit",
                        fontSize: "0.875rem",
                        lineHeight: 1.4,
                        mt: 1,
                      },
                    }}
                  />
                </ListItem>
                {index < laborItems.length - 1 && (
                  <Divider
                    component="li"
                    sx={{
                      backgroundColor: darkThemeEnabled
                        ? DARK_THEME_INPUT_OUTLINE_COLOR
                        : "inherited",
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </List>
        ) : loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size="50px" />
          </Box>
        ) : backdropOpen ? null : !projectId ? (
          <ThemedTypography
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            No project selected
          </ThemedTypography>
        ) : !selectedProposal?.id ? (
          <ThemedTypography
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            No labor items
          </ThemedTypography>
        ) : (
          <ThemedTypography
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            No labor items
          </ThemedTypography>
        )}
      </Box>
    </Box>
  );
};

export default MobileLaborTab;
